/* eslint-disable react/require-default-props */
import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useState,
} from 'react';
import { ReactElement } from 'react';

import { IoCloseSharp } from 'react-icons/io5';
import { toast } from 'react-toastify';

import { ModalInfo, Button, Input, Textarea } from '~/components';
import { useLoading } from '~/hooks/loading';
import { CollaboratorsService } from '~/services';
import { ICollaboratorEdit } from '~/services/Collaborators';

import * as S from './styles';

interface ICollaboratorModalProps {
  modalIsVisible: boolean;
  collaboratorId: number;
  userCollaboratorId: number;
  setModalIsVisible: Dispatch<SetStateAction<boolean>>;
  editForm: boolean;
}

export interface ICollaboratorChange {
  name: string;
  email: string;
  password: string;
  modules: string[];
  observations: string;
}

function CollaboratorModal({
  editForm,
  modalIsVisible,
  collaboratorId,
  userCollaboratorId,
  setModalIsVisible,
}: ICollaboratorModalProps): ReactElement {
  const { setLoading } = useLoading();

  const [isRemovingCollaborator, setIsRemovingCollaborator] = useState(false);
  const [collaboratorRole, setCollaboratorRole] = useState<ICollaboratorEdit>();
  const [collaborator, setCollaborator] = useState<ICollaboratorChange>({
    name: '',
    email: '',
    password: '',
    modules: [],
    observations: '',
  });

  const handleRemoveCollaborator = useCallback(async () => {
    try {
      setLoading(true);

      setIsRemovingCollaborator(true);
      const response = await CollaboratorsService.removeCollaborator(
        userCollaboratorId,
      );

      setLoading(false);
      if (response) toast.success('Colaborador removido com sucesso!');
    } catch (err) {
      console.log(err);
      toast.error('Houve um erro ao remover o colaborador');
      setLoading(false);
    } finally {
      setIsRemovingCollaborator(false);
      setModalIsVisible(false);
      setLoading(false);
    }
  }, [userCollaboratorId]);

  const handleGetCollaborator = useCallback(async () => {
    if (collaboratorId === -1) return;
    try {
      setLoading(true);

      const { data } = await CollaboratorsService.getOnlyCollaborator(
        collaboratorId,
      );

      if (data) setCollaboratorRole(data);

      setLoading(false);
    } catch (err) {
      console.log(err);
      setLoading(false);
    }
  }, [collaboratorId]);

  useEffect(() => {
    handleGetCollaborator();
  }, [collaboratorId, handleGetCollaborator]);

  const handleSubmit = useCallback(async () => {
    if (!collaborator) return;

    try {
      setLoading(true);

      if (editForm) {
        const response = await CollaboratorsService.updateCollaborator(
          collaboratorId,
          collaborator,
        );

        if (response) toast.success('Colaborador editado com sucesso!');
        setModalIsVisible(false);
      } else {
        const response = await CollaboratorsService.addCollaborator(
          collaborator,
        );
        if (response) toast.success('Colaborador criado com sucesso!');
        setModalIsVisible(false);
      }
      setLoading(false);
    } catch (err) {
      console.log(err);
      toast.error('So admins podem cadastrar colaboradores!');
      setLoading(false);
    }
  }, [collaborator, collaboratorId, editForm, setModalIsVisible]);

  useEffect(() => {
    if (collaboratorRole && editForm) {
      setCollaborator({
        email: collaboratorRole.collaborator.user.email,
        modules: collaboratorRole.collaborator.modules,
        name: collaboratorRole.collaborator.user.name,
        observations: collaboratorRole.collaborator.observations,
        password: collaboratorRole.dec_password,
      });
    } else {
      setCollaborator({
        name: '',
        email: '',
        password: '',
        modules: [],
        observations: '',
      });
    }
  }, [collaboratorRole, editForm]);

  return (
    <ModalInfo
      modalVisible={modalIsVisible}
      setModalVisible={setModalIsVisible}
      containerStyle={{
        justifyContent: 'flex-start',
        padding: '3rem',
        alignItems: 'flex-start',
      }}
    >
      <S.ConfirmDeleteContainer>
        <S.HeaderContainer>
          <S.ConfirmDeleteTitle>
            {editForm ? 'Colaborador' : 'Cadastrar Colaborador'}
          </S.ConfirmDeleteTitle>
          <IoCloseSharp
            style={{ cursor: 'pointer' }}
            size={30}
            onClick={() => setModalIsVisible(!modalIsVisible)}
          />
        </S.HeaderContainer>
        <S.Divider />
        <S.FormWrapper
          onSubmit={() => {
            console.log('teste');
          }}
        >
          {editForm ? (
            collaboratorRole &&
            collaboratorRole.collaborator.user && (
              <S.CollaboratorName>
                {collaboratorRole.collaborator.user.name}
              </S.CollaboratorName>
            )
          ) : (
            <Input
              name="collaborator_name"
              label="Nome"
              value={collaborator.name}
              onChange={({ target }) =>
                setCollaborator((prevState) => {
                  return { ...prevState, name: target.value };
                })
              }
            />
          )}
          <S.Body>
            <S.Side>
              <Input
                name="collaborator_email"
                label="Email"
                value={collaborator.email}
                onChange={({ target }) =>
                  setCollaborator((prevState) => {
                    return { ...prevState, email: target.value };
                  })
                }
              />
              <S.InputCheckboxWrapper>
                <S.InputCheckboxContainer>
                  <S.InputCheckbox
                    type="checkbox"
                    checked={
                      collaborator.modules &&
                      collaborator.modules.includes('módulo 1')
                    }
                    onClick={() =>
                      setCollaborator((prevState) => {
                        const moduleIndex = prevState.modules?.findIndex(
                          (item) => item === 'módulo 1',
                        );
                        const formatModule =
                          moduleIndex === -1
                            ? [...prevState.modules, 'módulo 1']
                            : prevState.modules?.filter(
                                (item) => item !== 'módulo 1',
                              );
                        return { ...prevState, modules: formatModule };
                      })
                    }
                  />
                  <S.InputCheckboxText>Treinadores</S.InputCheckboxText>
                </S.InputCheckboxContainer>
                <S.InputCheckboxContainer>
                  <S.InputCheckbox
                    type="checkbox"
                    checked={
                      collaborator.modules &&
                      collaborator.modules.includes('módulo 2')
                    }
                    onClick={() =>
                      setCollaborator((prevState) => {
                        const moduleIndex = prevState.modules?.findIndex(
                          (item) => item === 'módulo 2',
                        );
                        const formatModule =
                          moduleIndex === -1
                            ? [...prevState.modules, 'módulo 2']
                            : prevState.modules?.filter(
                                (item) => item !== 'módulo 2',
                              );
                        return { ...prevState, modules: formatModule };
                      })
                    }
                  />
                  <S.InputCheckboxText>I Lab</S.InputCheckboxText>
                </S.InputCheckboxContainer>
                <S.InputCheckboxContainer>
                  <S.InputCheckbox
                    type="checkbox"
                    checked={
                      collaborator.modules &&
                      collaborator.modules.includes('módulo 3')
                    }
                    onClick={() =>
                      setCollaborator((prevState) => {
                        const moduleIndex = prevState.modules?.findIndex(
                          (item) => item === 'módulo 3',
                        );
                        const formatModule =
                          moduleIndex === -1
                            ? [...prevState.modules, 'módulo 3']
                            : prevState.modules?.filter(
                                (item) => item !== 'módulo 3',
                              );
                        return { ...prevState, modules: formatModule };
                      })
                    }
                  />
                  <S.InputCheckboxText>I Boss</S.InputCheckboxText>
                </S.InputCheckboxContainer>
                <S.InputCheckboxContainer>
                  <S.InputCheckbox
                    type="checkbox"
                    checked={
                      collaborator.modules &&
                      collaborator.modules.includes('módulo 4')
                    }
                    onClick={() =>
                      setCollaborator((prevState) => {
                        const moduleIndex = prevState.modules?.findIndex(
                          (item) => item === 'módulo 4',
                        );
                        const formatModule =
                          moduleIndex === -1
                            ? [...prevState.modules, 'módulo 4']
                            : prevState.modules?.filter(
                                (item) => item !== 'módulo 4',
                              );
                        return { ...prevState, modules: formatModule };
                      })
                    }
                  />
                  <S.InputCheckboxText>Colaboradores</S.InputCheckboxText>
                </S.InputCheckboxContainer>
                <S.InputCheckboxContainer>
                  <S.InputCheckbox
                    type="checkbox"
                    checked={
                      collaborator.modules &&
                      collaborator.modules.includes('módulo 5')
                    }
                    onClick={() =>
                      setCollaborator((prevState) => {
                        const moduleIndex = prevState.modules?.findIndex(
                          (item) => item === 'módulo 5',
                        );
                        const formatModule =
                          moduleIndex === -1
                            ? [...prevState.modules, 'módulo 5']
                            : prevState.modules?.filter(
                                (item) => item !== 'módulo 5',
                              );

                        return { ...prevState, modules: formatModule };
                      })
                    }
                  />
                  <S.InputCheckboxText>Relatórios</S.InputCheckboxText>
                </S.InputCheckboxContainer>
              </S.InputCheckboxWrapper>
            </S.Side>
            <S.Side>
              <Input
                name="collaborator_password"
                label="Senha"
                type="password"
                value={collaborator.password}
                onChange={({ target }) =>
                  setCollaborator((prevState) => {
                    return { ...prevState, password: target.value };
                  })
                }
              />
              <S.TextareaText>Observação</S.TextareaText>
              <Textarea
                name="Observação"
                value={collaborator.observations}
                onChange={({ target }) =>
                  setCollaborator((prevState) => {
                    return { ...prevState, observations: target.value };
                  })
                }
              />
            </S.Side>
          </S.Body>
        </S.FormWrapper>
        <S.ConfirmDeleteButtonGroup>
          {editForm && (
            <Button
              size="small"
              textContent="Remover Colaborador"
              onClick={handleRemoveCollaborator}
              variant="red"
              extraStyles={{
                maxWidth: '200px',
                maxHeight: 35,
              }}
            />
          )}
          <Button
            size="small"
            textContent={editForm ? 'Salvar' : 'Cadastrar'}
            onClick={handleSubmit}
            variant="black"
            extraStyles={{
              maxWidth: '200px',
              maxHeight: 35,
              marginLeft: 17,
            }}
          />
        </S.ConfirmDeleteButtonGroup>
      </S.ConfirmDeleteContainer>
    </ModalInfo>
  );
}

export default CollaboratorModal;
